import { useState } from "react";
import {
  Activity,
  BatteryCharging,
  CheckCircle2,
  Copy,
  HardDrive,
  KeyRound,
  LogOut,
  MemoryStick,
  Save,
  ShieldCheck,
  Thermometer,
  UserRoundCog,
  WifiOff,
} from "lucide-react";
import { portalApi, type PortalApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Dialog, EmptyState, ErrorState, LoadingState, PageHeader } from "../components/Shared";
import { useAsyncData } from "../hooks/useAsyncData";
import type {
  AccountSummary,
  AdminSession,
  PortalConfig,
  RenderSubtask,
  Worker,
  WorkerRole,
} from "../types";

const WORKER_ROLES: Array<{ value: WorkerRole; label: string }> = [
  { value: "whole-job", label: "Whole jobs" },
  { value: "render", label: "Rendering" },
];

function bytesToGb(value: number | null): string {
  return value === null ? "Unknown" : `${(value / 1024 ** 3).toFixed(0)} GB`;
}

function bytesToSize(value: number): string {
  return value >= 1024 ** 3
    ? `${(value / 1024 ** 3).toFixed(1)} GB`
    : `${(value / 1024 ** 2).toFixed(0)} MB`;
}

function renderProgress(subtask: RenderSubtask): string {
  const percent = subtask.progress.progressPercent;
  if (typeof percent === "number") return `${percent.toFixed(1)}%`;
  return subtask.renderAttemptStatus ?? subtask.status;
}

function sessionDevice(userAgent: string | null): string {
  if (!userAgent) return "Unknown browser";
  const browser = userAgent.includes("Edg/")
    ? "Edge"
    : userAgent.includes("Firefox/")
      ? "Firefox"
      : userAgent.includes("Chrome/")
        ? "Chrome"
        : userAgent.includes("Safari/")
          ? "Safari"
          : "Browser";
  const device = userAgent.includes("Windows")
    ? "Windows"
    : userAgent.includes("Mac OS X")
      ? "macOS"
      : userAgent.includes("iPhone") || userAgent.includes("iPad")
        ? "iOS"
        : userAgent.includes("Android")
          ? "Android"
          : userAgent.includes("Linux")
            ? "Linux"
            : "unknown device";
  return `${browser} on ${device}`;
}

export function WorkersPage({ client = portalApi }: { client?: PortalApiClient }) {
  const workers = useAsyncData(() => client.admin.workers(), [client]);
  const renderSubtasks = useAsyncData(() => client.admin.renderSubtasks(), [client]);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function update(worker: Worker, input: { enabled?: boolean; authorizedRoles?: WorkerRole[] }) {
    setSaving(worker.id);
    setError(null);
    try {
      await client.admin.updateWorker(worker.id, input);
      await workers.reload();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "The worker could not be updated.");
    } finally {
      setSaving(null);
    }
  }

  if (workers.loading) return <LoadingState label="Loading workers" />;
  if (workers.error || !workers.data) {
    return <ErrorState message={workers.error ?? "Workers are unavailable."} onRetry={workers.reload} />;
  }

  return (
    <>
      <PageHeader title="Workers" description="Production capacity and health. Staff cannot see this view." />
      {error ? <div className="inline-alert" role="alert">{error}</div> : null}
      {workers.data.length ? (
        <section className="worker-list">
          {workers.data.map((worker) => {
            const usedMemory =
              worker.memoryTotalBytes !== null && worker.memoryAvailableBytes !== null
                ? Math.max(0, 100 - (worker.memoryAvailableBytes / worker.memoryTotalBytes) * 100)
                : null;
            return (
              <article className="worker-row" key={worker.id}>
                <div className="worker-identity">
                  <span className={`health-dot health-${worker.enabled ? worker.health : "disabled"}`} />
                  <div>
                    <strong>{worker.name}</strong>
                    <span>{worker.platform ?? "Unknown"} {worker.architecture ?? ""} / {worker.mode}</span>
                    <span>Build {worker.build ?? "unknown"}</span>
                  </div>
                </div>
                <div className="worker-stat">
                  <Activity size={16} />
                  <span>{worker.activeRenderStage ?? worker.activeStage ?? "Idle"} / {worker.activeAttempts + worker.activeRenderAttempts} active</span>
                </div>
                <div className="worker-stat">
                  <HardDrive size={16} />
                  <span>{bytesToGb(worker.diskFreeBytes)} free{worker.diskTotalBytes ? ` of ${bytesToGb(worker.diskTotalBytes)}` : ""}</span>
                </div>
                <div className="worker-stat">
                  <MemoryStick size={16} />
                  <span>{usedMemory === null ? "Memory unknown" : `${usedMemory.toFixed(0)}% memory`} / {worker.memoryPressure}</span>
                </div>
                <div className="worker-stat">
                  <Thermometer size={16} />
                  <span>{worker.thermalState}{worker.thermalCelsius === null ? "" : ` / ${worker.thermalCelsius.toFixed(1)} C`}</span>
                </div>
                <div className="worker-stat">
                  <BatteryCharging size={16} />
                  <span>{worker.powerSource}{worker.batteryPercent === null ? "" : ` / ${worker.batteryPercent.toFixed(0)}%`}</span>
                </div>
                <div className="lane-summary">
                  {Object.entries(worker.laneLimits).map(([lane, limit]) => <span key={lane}>{lane} {limit}</span>)}
                  <span>remote {worker.remoteRenderEnabled ? worker.renderSlots : 0}</span>
                </div>
                <div className="checkbox-stack" aria-label={`Authorized roles for ${worker.name}`}>
                  {WORKER_ROLES.map((role) => (
                    <label key={role.value}>
                      <input
                        type="checkbox"
                        checked={worker.roles.includes(role.value)}
                        disabled={saving === worker.id}
                        onChange={(event) => {
                          const authorizedRoles = event.target.checked
                            ? [...new Set([...worker.roles, role.value])]
                            : worker.roles.filter((item) => item !== role.value);
                          void update(worker, { authorizedRoles });
                        }}
                      />
                      {role.label}
                    </label>
                  ))}
                </div>
                <label className="toggle-control">
                  <input type="checkbox" checked={worker.enabled} disabled={saving === worker.id} onChange={(event) => void update(worker, { enabled: event.target.checked })} />
                  <span>{worker.enabled ? "Enabled" : "Disabled"}</span>
                </label>
              </article>
            );
          })}
        </section>
      ) : (
        <EmptyState title="No workers enrolled" message="Enrolled worker machines will appear here." />
      )}
      <PageHeader
        title="Delegated renders"
        description="Fenced render attempts, transfer checks, machine pressure, and cleanup. Owner only."
      />
      {renderSubtasks.loading ? <LoadingState label="Loading delegated renders" /> : null}
      {renderSubtasks.error ? (
        <ErrorState message={renderSubtasks.error} onRetry={renderSubtasks.reload} />
      ) : null}
      {renderSubtasks.data?.length ? (
        <div className="table-wrap">
          <table className="render-monitor-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Route</th>
                <th>Attempt</th>
                <th>Progress</th>
                <th>Machine</th>
                <th>Result and cleanup</th>
              </tr>
            </thead>
            <tbody>
              {renderSubtasks.data.map((subtask) => (
                <tr key={subtask.id}>
                  <td>
                    <strong>{subtask.jobTitle}</strong>
                    <span className="table-subtext">{subtask.manifestSha256.slice(0, 12)} / {bytesToSize(subtask.manifestByteCount)}</span>
                  </td>
                  <td>
                    {subtask.ownerWorkerName} to {subtask.renderWorkerName ?? "Unclaimed"}
                  </td>
                  <td>
                    #{subtask.attemptNumber ?? "-"} / {subtask.renderAttemptStatus ?? subtask.status}
                    <span className="table-subtext">{subtask.leaseExpiresAt ? `Lease ${new Date(subtask.leaseExpiresAt).toLocaleTimeString()}` : "No live lease"}</span>
                  </td>
                  <td>
                    <strong>{renderProgress(subtask)}</strong>
                    <span className="table-subtext">{subtask.progress.stage ?? "Waiting"}</span>
                  </td>
                  <td>
                    {subtask.renderWorkerThermalState ?? "No telemetry"}
                    {subtask.renderWorkerThermalCelsius === null ? "" : ` / ${subtask.renderWorkerThermalCelsius.toFixed(1)} C`}
                    <span className="table-subtext">
                      {subtask.renderWorkerMemoryPressure ?? "memory unknown"} / {subtask.renderWorkerPowerSource ?? "power unknown"}
                    </span>
                  </td>
                  <td>
                    {subtask.outputSha256 ? `MP4 ${subtask.outputSha256.slice(0, 12)}` : "No result"}
                    <span className="table-subtext">Cleanup {subtask.cleanupStatus ?? "pending"}</span>
                    {subtask.attemptFailureDetail || subtask.failureDetail ? (
                      <span className="table-subtext error-text">
                        {subtask.attemptFailureDetail ?? subtask.failureDetail}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : renderSubtasks.data ? (
        <EmptyState title="No delegated renders" message="Remote render attempts will appear here." />
      ) : null}
    </>
  );
}

export function AccountsPage({ client = portalApi }: { client?: PortalApiClient }) {
  const accounts = useAsyncData(() => client.admin.users(), [client]);
  const [oneTimePassword, setOneTimePassword] = useState<{ account: AccountSummary; password: string } | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  async function reset(account: AccountSummary) {
    setSaving(account.id);
    try {
      const result = await client.admin.resetPassword(account.id);
      setOneTimePassword({ account, password: result.oneTimePassword });
      await accounts.reload();
    } finally {
      setSaving(null);
    }
  }

  if (accounts.loading) return <LoadingState label="Loading accounts" />;
  if (accounts.error || !accounts.data) {
    return <ErrorState message={accounts.error ?? "Accounts are unavailable."} onRetry={accounts.reload} />;
  }

  return (
    <>
      <PageHeader title="Accounts" description="Individual staff access. There is no public registration." />
      <div className="table-wrap">
        <table>
          <thead><tr><th>Person</th><th>Role</th><th>Security</th><th>Account state</th><th className="actions-cell">Password</th></tr></thead>
          <tbody>
            {accounts.data.map((account) => (
              <tr key={account.id}>
                <td><strong>{account.displayName}</strong><span className="table-subtext">{account.email}</span></td>
                <td className="capitalize">{account.role}</td>
                <td>{account.mustChangePassword ? "Password change required" : "Password set"}{account.role === "owner" ? ` / ${account.totpEnabled ? "2FA active" : "2FA required"}` : ""}</td>
                <td>{account.disabledAt ? "Disabled" : "Enabled"}</td>
                <td className="actions-cell">
                  <button className="button secondary small" type="button" disabled={saving === account.id} onClick={() => void reset(account)}>
                    <KeyRound size={15} />
                    Reset password
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {oneTimePassword ? (
        <Dialog title="One-time password" onClose={() => setOneTimePassword(null)} actions={<button className="button primary" type="button" onClick={() => setOneTimePassword(null)}>Done</button>}>
          <p>Give this password to {oneTimePassword.account.displayName} through a private channel. It is shown only once.</p>
          <div className="secret-display">
            <code>{oneTimePassword.password}</code>
            <button className="icon-button" type="button" title="Copy password" aria-label="Copy one-time password" onClick={() => void navigator.clipboard.writeText(oneTimePassword.password)}><Copy size={17} /></button>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}

export function SecurityPage({ client = portalApi }: { client?: PortalApiClient }) {
  const { session } = useAuth();
  const sessions = useAsyncData(() => client.admin.sessions(), [client]);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function revoke(target: AdminSession) {
    setSaving(target.id);
    setError(null);
    try {
      await client.admin.revokeSession(target.id);
      await sessions.reload();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "The session could not be revoked.");
    } finally {
      setSaving(null);
    }
  }

  return (
    <>
      <PageHeader title="Security" description="Owner authentication and active staff sessions." />
      <section className="security-summary">
        <article>
          <ShieldCheck size={20} />
          <div><strong>Authenticator app</strong><span>{session?.user.totpVerified ? "Enabled and verified" : "Setup required"}</span></div>
          {session?.user.totpVerified ? <CheckCircle2 size={18} className="text-success" /> : null}
        </article>
        <article><UserRoundCog size={20} /><div><strong>Role</strong><span>Owner access</span></div></article>
        <article><KeyRound size={20} /><div><strong>Session expires</strong><span>{session ? new Date(session.expiresAt).toLocaleString() : "Unknown"}</span></div></article>
      </section>
      <PageHeader title="Active sessions" description="Signed-in staff devices with access to the portal." />
      {error ? <div className="inline-alert" role="alert">{error}</div> : null}
      {sessions.loading ? <LoadingState label="Loading active sessions" /> : null}
      {sessions.error ? <ErrorState message={sessions.error} onRetry={sessions.reload} /> : null}
      {sessions.data?.length ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Account</th>
                <th>Device and browser</th>
                <th>Signed in</th>
                <th>Last active</th>
                <th>Expires</th>
                <th className="actions-cell">Session</th>
              </tr>
            </thead>
            <tbody>
              {sessions.data.map((activeSession) => {
                const current = activeSession.current || activeSession.id === session?.id;
                const device = sessionDevice(activeSession.userAgent);
                return (
                  <tr key={activeSession.id}>
                    <td>
                      <strong>{activeSession.displayName}</strong>
                      <span className="table-subtext">{activeSession.email} / {activeSession.role}</span>
                    </td>
                    <td title={activeSession.userAgent ?? undefined}>
                      {device}
                      <span className="table-subtext">{activeSession.ipAddress ?? "IP unavailable"}</span>
                    </td>
                    <td>{new Date(activeSession.createdAt).toLocaleString()}</td>
                    <td>{new Date(activeSession.lastSeenAt).toLocaleString()}</td>
                    <td>{new Date(activeSession.expiresAt).toLocaleString()}</td>
                    <td className="actions-cell">
                      {current ? (
                        <span className="table-subtext text-success">This device</span>
                      ) : (
                        <button
                          className="button secondary small"
                          type="button"
                          disabled={saving === activeSession.id}
                          aria-label={`Revoke session for ${activeSession.displayName} on ${device}`}
                          onClick={() => void revoke(activeSession)}
                        >
                          <LogOut size={15} />
                          Revoke session
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : sessions.data ? (
        <EmptyState title="No active sessions" message="Signed-in staff devices will appear here." />
      ) : null}
    </>
  );
}

export function SystemPage({ client = portalApi }: { client?: PortalApiClient }) {
  const config = useAsyncData(() => client.admin.config(), [client]);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<PortalConfig | null>(null);

  if (config.loading) return <LoadingState label="Loading system configuration" />;
  if (config.error || !config.data) return <ErrorState message={config.error ?? "System configuration is unavailable."} onRetry={config.reload} />;
  const value = draft ?? config.data;

  async function save() {
    setSaving(true);
    try {
      await client.admin.updateConfig({
        canaryMode: value.canaryMode,
        trelloBridgeEnabled: value.trelloBridgeEnabled,
        normalBatchesEnabled: value.normalBatchesEnabled,
      });
      setDraft(null);
      await config.reload();
    } finally {
      setSaving(false);
    }
  }

  const set = (input: Partial<PortalConfig>) => setDraft({ ...value, ...input });
  return (
    <>
      <PageHeader title="System" description="Owner-only rollout and bridge controls." actions={<button className="button primary" type="button" disabled={saving || !draft} onClick={() => void save()}><Save size={16} />Save changes</button>} />
      <section className="content-section">
        <div className="settings-list">
          <label className="toggle-control"><input type="checkbox" checked={value.canaryMode} onChange={(event) => set({ canaryMode: event.target.checked })} /><span>Canary mode</span></label>
          <label className="toggle-control"><input type="checkbox" checked={value.trelloBridgeEnabled} onChange={(event) => set({ trelloBridgeEnabled: event.target.checked })} /><span>Trello bridge enabled</span></label>
          <label className="toggle-control"><input type="checkbox" checked={value.normalBatchesEnabled} onChange={(event) => set({ normalBatchesEnabled: event.target.checked })} /><span>Normal batches enabled</span></label>
        </div>
      </section>
    </>
  );
}

export function AuditPage({ client = portalApi }: { client?: PortalApiClient }) {
  const audit = useAsyncData(() => client.admin.audit(), [client]);
  if (audit.loading) return <LoadingState label="Loading audit history" />;
  if (audit.error || !audit.data) return <ErrorState message={audit.error ?? "Audit history is unavailable."} onRetry={audit.reload} />;
  return (
    <>
      <PageHeader title="Audit" description="Recent owner, staff, and worker actions recorded by the server." />
      {audit.data.records.length ? (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Time</th><th>Action</th><th>Actor</th><th>Target</th></tr></thead>
            <tbody>
              {audit.data.records.map((record) => (
                <tr key={record.id}>
                  <td>{new Date(record.createdAt).toLocaleString()}</td>
                  <td>{record.action}</td>
                  <td>{record.actorId ? `${record.actorType} / ${record.actorId}` : record.actorType || "System"}</td>
                  <td>{[record.entityType, record.entityId].filter(Boolean).join(" / ") || "None"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState title="No audit records" message="Recorded actions will appear here." />}
    </>
  );
}

export function NotAllowedPage() {
  return <ErrorState message="Your account does not have permission to open this page." offline={false} />;
}

export function OfflinePage() {
  return <ErrorState message="SwipeCut cannot reach the portal server. Your current page will reconnect automatically." offline />;
}

export function NoRoutePage() {
  return <EmptyState title="Page not found" message="The page may have moved or your account may not have access." action={<WifiOff size={20} />} />;
}
